<template>
  <div v-show="loadingOverlay.show" class="fixed top-0 bottom-0 left-0 right-0 z-50 flex flex-col items-center justify-center w-full h-screen overflow-hidden bg-white/95 dark:bg-black/90">
    <loading-spinner class="w-10 h-10 dark:stroke-white sm:h-16 sm:w-16" />
    <h2 class="mt-5 text-xl font-semibold text-center text-gray-900 dark:text-white">{{ loadingOverlay.header }}</h2>
    <p class="px-4 my-3 text-center text-gray-900 dark:text-white sm:w-1/2">{{ loadingOverlay.subHeader }}</p>
  </div>
  <div class="z-10 flex-col justify-center mb-10 sm:px-6 md:flex lg:px-5">
    <div class="px-4 mt-5 sm:mx-auto sm:w-full sm:max-w-md">
      <div class="relative bg-white border-gray-300 rounded-md shadow-lg pt-14 dark:bg-gray-900 sm:top-20">
        <!-- Icon -->
        <div class="absolute inset-x-0 flex justify-center top-8">
          <div class="relative w-auto px-1">
            <router-link to="/">
              <IconBlackSvg class="rounded-full shadow-lg h-14 w-14 dark:shadow-purple-800/50" />
            </router-link>
          </div>
        </div>

        <!-- Header -->
        <h2 v-if="extensionAuth && isExtensionInstalled && !newUser" class="text-2xl font-extrabold text-center text-gray-900 pt-14 dark:text-white md:text-3xl">Link Extension With Account</h2>
        <h2 v-else class="text-3xl font-extrabold text-center text-gray-900 pt-14 dark:text-white">
          {{ newUser ? "Sign Up" : "Login" }}
        </h2>

        <!-- Login/Sign Up Toggle -->
        <div class="flex mt-1 sm:ml-0" v-if="newUser">
          <p class="w-full text-center">
            <span class="text-sm text-gray-400 dark:text-gray-500">Already signed up?</span>
            <a class="ml-1 text-sm text-blue-400" href="#" @click="newUser = false">Login</a>
          </p>
        </div>

        <div class="flex mt-1 sm:ml-0" v-else>
          <p class="w-full text-center">
            <span class="text-sm text-gray-400 dark:text-gray-500">New here?</span>
            <a class="ml-1 text-sm text-blue-400" href="#" @click="newUser = true">Sign Up</a>
          </p>
        </div>

        <!-- Form -->
        <div class="mt-6 sm:mx-auto sm:w-full sm:max-w-md">
          <div class="px-4 py-4 sm:rounded-b-md sm:px-10">
            <div class="block">
              <button
                @click="googleAuth"
                class="inline-flex items-center w-full px-4 py-2 font-medium text-gray-600 border-gray-100 rounded-md shadow-md text-md bg-gray-white border-1 hover:bg-gray-50 hover:shadow-md dark:bg-gray-600 dark:text-white dark:hover:bg-gray-700 sm:text-lg"
              >
                <svg viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
                  <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
                    <path
                      fill="#4285F4"
                      d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z"
                    />
                    <path
                      fill="#34A853"
                      d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z"
                    />
                    <path
                      fill="#EA4335"
                      d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z"
                    />
                  </g>
                </svg>
                <span class="ml-4">Continue with Google</span>
              </button>
            </div>
            <div>
              <div class="relative my-8 md:my-10">
                <div class="absolute inset-0 flex items-center">
                  <div class="w-full border-t border-gray-300 dark:border-gray-600"></div>
                </div>
                <div class="relative flex justify-center text-sm">
                  <span class="px-2 text-gray-400 bg-white rounded-md dark:bg-gray-900 dark:text-gray-400"> Or continue with </span>
                </div>
              </div>

              <Form @submit="emailAuth">
                <div class="grid grid-cols-6 gap-4 md:gap-6">
                  <div v-if="newUser" class="col-span-6 sm:col-span-3">
                    <label for="first-name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">First name</label>
                    <Field
                      type="text"
                      name="first-name"
                      v-model="firstName"
                      autocomplete=""
                      class="block w-full mt-1 bg-transparent border-gray-300 rounded-md shadow-sm focus:border-slate-500 focus:ring-slate-500 dark:text-white sm:text-sm"
                      :rules="validateName"
                    />
                    <ErrorMessage class="mt-1 text-sm text-red-500" name="first-name" />
                  </div>

                  <div v-if="newUser" class="col-span-6 sm:col-span-3">
                    <label for="last-name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Last name</label>
                    <Field
                      type="text"
                      name="last-name"
                      v-model="lastName"
                      id="last-name"
                      autocomplete="family-name"
                      class="block w-full mt-1 bg-transparent border-gray-300 rounded-md shadow-sm focus:border-slate-500 focus:ring-slate-500 dark:text-white sm:text-sm"
                      :rules="validateName"
                    />
                    <ErrorMessage class="mt-1 text-sm text-red-500" name="last-name" />
                  </div>

                  <div class="col-span-6">
                    <label for="email" class="block text-sm font-medium text-gray-700 dark:text-gray-300"> Email address </label>
                    <div class="mt-1">
                      <Field
                        id="email"
                        name="email"
                        v-model="email"
                        type="email"
                        autocomplete="email"
                        required
                        class="block w-full px-3 py-2 placeholder-gray-400 bg-transparent border border-gray-300 rounded-md shadow-sm appearance-none focus:border-slate-500 focus:outline-none focus:ring-slate-500 dark:text-white sm:text-sm"
                        :rules="validateEmail"
                      />
                      <ErrorMessage class="mt-1 text-sm text-red-500" name="email" />
                    </div>
                  </div>
                </div>

                <div class="mt-6">
                  <button type="submit" class="flex w-full btn-primary" ref="submitButton">
                    <span>{{ newUser ? "Sign Up" : "Login" }}</span>
                    <loading-spinner class="w-5 h-5" v-if="loading" />
                  </button>

                  <div class="col-span-6 mt-5 text-xs text-gray-500">
                    <p v-if="!newUser">Authentication link will be sent to your email.</p>

                    <p v-if="newUser">
                      By signing up you accept Wisery's
                      <router-link to="/tos" class="underline"> Terms of Service</router-link> &
                      <router-link to="/privacy" class="underline">Privacy Policy.</router-link>
                    </p>
                  </div>
                </div>
              </Form>
            </div>
          </div>
          <div v-if="emailSentModal">
            <modal-view :theme="'success'" :title="'Email Sent'" :description="'Click on the authentication link in the email.'" :numButtons="0" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getAuth, signInWithPopup, GoogleAuthProvider, sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink, updateProfile } from "firebase/auth";
import { db } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import ModalView from "./ModalView.vue";
import LoadingSpinner from "./LoadingSpinner.vue";
import { Form, Field, ErrorMessage } from "vee-validate";
import { useRoute, useRouter } from "vue-router";
import IconBlackSvg from "./logos/IconBlackSvg";
import { doc, setDoc, getDocs, query, collection, where, Timestamp } from "firebase/firestore";
// import { getGender } from "gender-detection-from-name";
import { ref, watch } from "vue";
import { detect } from "detect-browser";
import utilFunctions from "../composables/util";
import { useToast } from "vue-toastification";
import chromeExtension from "../composables/chromeExtension";
export default {
  components: { ModalView, Form, Field, ErrorMessage, LoadingSpinner, IconBlackSvg },
  setup() {
    const newUser = ref(true);
    const firstName = ref("");
    const lastName = ref("");
    const email = ref("");
    const emailSentModal = ref(false);
    const loading = ref(false);
    const loadingOverlay = ref({
      show: false,
      header: "Loading...",
      subHeader: "",
    });
    const extensionAuth = ref(false);
    const router = useRouter();
    const route = useRoute();
    const auth = getAuth();
    const { debugging, extensionInstalled, getUser } = utilFunctions();
    const toast = useToast();
    const isExtensionInstalled = ref(false);
    const referrer = ref(null);

    try {
      extensionInstalled(chromeExtension.id, chromeExtension.image, (installed) => {
        isExtensionInstalled.value = installed;
      });
    } catch (error) {
      console.log(error);
    }

    if (route.query.login) newUser.value = false;
    if (route.query.ref) referrer.value = route.query.ref || null;

    // auth triggered from extension?
    if (route.query.originTab) {
      newUser.value = true;
      extensionAuth.value = true;
    }

    if (isSignInWithEmailLink(auth, window.location.href)) {
      loadingOverlay.value = {
        show: true,
        header: "Authenticating...",
        subHeader: "This may take a few seconds. Please do not close the tab.",
      };

      let email = window.localStorage.getItem("emailForSignIn");
      if (!email && auth.currentUser) {
        // User opened the link on a different device.
        email = window.prompt("Please provide your email for confirmation");
      }

      signInWithEmailLink(auth, email, window.location.href)
        .then(async (result) => {
          const user = result.user;

          // get token and send it to extension
          if (extensionAuth.value || isExtensionInstalled) {
            const authToken = await getAuthToken(user.uid);
            if (authToken) {
              window.postMessage({ type: "WISERY_WEB", token: authToken }, "*");
            }
          }

          let existingUser;

          try {
            existingUser = await getUser(user.uid);
          } catch (error) {
            toast.error("Error creating account");
            console.log(error);
            await debugging({
              function: "signInWithEmailLink",
              error: error,
              context: "error getting user getUser",
            });
          }

          if ((!existingUser || !existingUser.firstName) && window.localStorage.getItem("firstName")) {
            const ipDataResult = await ipData();
            const newUser = {
              firstName: window.localStorage.getItem("firstName"),
              lastName: window.localStorage.getItem("lastName"),
              language: navigator.language,
              createdAt: Timestamp.fromDate(new Date(user.metadata.creationTime)),
              email: user.email,
              ipData: ipDataResult,
              referrer: referrer.value,
              channels: {
                chrome: {
                  id: null,
                  notifications: null,
                },
                email: {
                  id: user.email,
                  notifications: true,
                },
                sms: {
                  id: null,
                  notifications: false,
                },
                whatsapp: {
                  id: null,
                  notifications: false,
                },
              },
              browser: browser(),
            };

            try {
              await setDoc(doc(db, "users", user.uid), newUser, { merge: true });
            } catch (error) {
              toast.error("Error creating account");
              await debugging({
                function: "signInWithEmailLink",
                error: error,
                context: "error creating user",
              });
              return;
            }
          }

          // redirects user to website from where extension was triggered when logged out
          loginTriggeredByExtension();

          loadingOverlay.value.show = false;

          router.push("/");
        })
        .then(async () => {
          // update user profile
          updateProfile(auth.currentUser, {
            displayName: `${window.localStorage.getItem("firstName")} ${window.localStorage.getItem("lastName")}`,
          }).catch((error) => {
            console.log(error);
          });
        })
        .then(() => {
          window.localStorage.removeItem("emailForSignIn");
          window.localStorage.removeItem("firstName");
          window.localStorage.removeItem("lastName");
        })
        .catch(async (error) => {
          console.log(error);
          // Some error occurred, you can inspect the code: error.code
          // Common errors could be invalid email and invalid or expired OTPs.
          toast.error("Error creating account.\nPlease open auth link from email in the same browser window from where you initiated the Wisery sign up.", {
            timeout: 15000,
          });

          loadingOverlay.value.show = false;

          await debugging({
            function: "signInWithEmailLink",
            error: error,
            context: "cathing error",
          });
        });
    }

    async function ipData() {
      let data = null;
      try {
        const response = await fetch("https://ipapi.co/json/");
        data = await response.json();
        data.language = window.navigator.userLanguage || window.navigator.language;
      } catch (error) {
        console.log("error", error);
      }
      return data;
    }

    async function getAuthToken(uid) {
      try {
        const functions = getFunctions();
        const createAuthToken = httpsCallable(functions, "createAuthToken");

        const response = await createAuthToken({ uid: uid });

        return response.data.customToken;
      } catch (error) {
        await debugging({
          function: "getAuthToken",
          error: error,
        });
        return false;
      }
    }

    const urlQuery = ref(route.query);
    watch(
      () => urlQuery,
      (query) => {
        urlQuery.value = query;
      }
    );

    function loginTriggeredByExtension() {
      if (urlQuery.value.textSelection && urlQuery.value.originTab) {
        window.postMessage(
          {
            type: "WISERY_WEB",
            origin: {
              tab: urlQuery.value.originTab,
              textSelection: urlQuery.value.textSelection,
            },
          },
          "*"
        );
      }
    }

    function browser() {
      try {
        const browser = detect();

        if (browser) {
          return {
            name: browser.name,
            version: browser.version,
            os: browser.os,
          };
        } else {
          return null;
        }
      } catch (error) {
        return null;
      }
    }

    function googleAuth() {
      loadingOverlay.value.show = true;
      loadingOverlay.value.header = "Waiting for Google authentication...";

      const provider = new GoogleAuthProvider();

      signInWithPopup(auth, provider)
        .then(async (result) => {
          loadingOverlay.value.header = "Authorizing Wisery account...";
          loadingOverlay.value.subHeader = "This may take a few seconds, please don't close this page.";
          const user = result.user;

          // Get token and send it to extension
          if (extensionAuth.value) {
            try {
              const authToken = await getAuthToken(user.uid);
              if (authToken) {
                window.postMessage({ type: "WISERY_WEB", token: authToken }, "*");
              } else {
                await debugging({
                  function: "if extensionAuth.value",
                  error: "no authToken",
                  file: "AuthSignUp.vue",
                });
              }
            } catch (error) {
              await debugging({
                function: "if extensionAuth.value",
                error: error,
                file: "AuthSignUp.vue",
              });
            }
          }

          let existingUser;
          try {
            existingUser = await getUser(user.uid);
          } catch (error) {
            toast.error("Error creating account");
            console.log(error);
            await debugging({
              function: "googleAuth",
              error: error,
              context: "error getting user getUser",
            });
          }

          if (!existingUser || !existingUser.firstName) {
            const ipDataResult = await ipData();

            const newUser = {
              profileImgUrl: user.photoURL,
              createdAt: Timestamp.fromDate(new Date(user.metadata.creationTime)),
              firstName: user.displayName ? user.displayName.split(" ")[0] : "",
              lastName: user.displayName ? user.displayName.split(" ")[1] : "",
              email: user.email,
              language: navigator.language,
              ipData: ipDataResult,
              referrer: referrer.value,
              channels: {
                chrome: {
                  id: null,
                  notifications: null,
                },
                email: {
                  id: user.email,
                  notifications: true,
                },
                sms: {
                  id: null,
                  notifications: false,
                },
                whatsapp: {
                  id: null,
                  notifications: false,
                },
              },
              browser: browser(),
            };
            try {
              await setDoc(doc(db, "users", user.uid), newUser, { merge: true });
            } catch (error) {
              toast.error("Error creating account");
              console.log(error);
              return;
            }
          }

          // redirects user to website from where extension was triggered when logged out
          loginTriggeredByExtension();

          loadingOverlay.value.show = false;

          router.push("/");
        })
        .catch(async (error) => {
          await debugging({
            function: "googleAuth",
            error: error,
          });
        });
    }

    async function doesUserExist(email) {
      try {
        const q = query(collection(db, "users"), where("email", "==", email));

        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          return false;
        } else {
          return true;
        }
      } catch (error) {
        console.log(error.message);
      }
    }

    async function emailAuth() {
      if (!newUser.value) {
        // user is logging in, NOT signing up
        const userExists = await doesUserExist(email.value);
        // check if user exists
        if (!userExists) {
          // user is trying to login without having signed up
          toast.error("Account doesn't exist. Please sign up first.");

          return;
        }
      }

      // eslint-disable-next-line no-unreachable
      loading.value = true;

      const url = location.hostname === "localhost" ? "http://localhost:8080/auth" : "https://wisery.io/auth";

      const actionCodeSettings = {
        url: url,
        handleCodeInApp: true,
      };

      sendSignInLinkToEmail(auth, email.value, actionCodeSettings)
        .then(() => {
          // link sent
          loading.value = false;
          emailSentModal.value = true;

          // Save the email locally so you don't need to ask the user for it again
          // if they open the link on the same device.
          window.localStorage.setItem("emailForSignIn", email.value);
          window.localStorage.setItem("firstName", firstName.value);
          window.localStorage.setItem("lastName", lastName.value);
        })
        .catch((error) => {
          console.log(error);
        });
    }

    function validateName(value) {
      if (!value) {
        return "Your name is required";
      }

      if (value.length < 2) {
        return "Please use your full name";
      }

      return true;
    }

    function validateEmail(value) {
      // if the field is empty
      if (!value) {
        return "This field is required";
      }

      // if the field is not a valid email
      if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)) {
        return "Valid email required";
      }

      // All is good
      return true;
    }

    return {
      newUser,
      firstName,
      lastName,
      email,
      emailSentModal,
      loading,
      extensionAuth,
      googleAuth,
      emailAuth,
      validateName,
      validateEmail,
      loadingOverlay,
      isExtensionInstalled,
    };
  },
};

// async function loggedOutUserTasks(ip) {
//   const taskQuery = query(collection(db, "tasks"), where("user", "==", ip));
//   const tasks = await getDocs(taskQuery);
//   if (tasks.empty) {
//     return false;
//   } else {
//     let taskList = [];
//     tasks.forEach((doc) => {
//       taskList.push(doc.data());
//     });
//     return taskList;
//   }
// }

// async function updateUserIdOnTasks(tasks, userId) {
//   if (tasks.length) {
//     await Promise.all(
//       tasks.map(async (task) => {
//         task.user = userId;
//         await updateDoc(doc(db, "tasks", task.id), task);
//       })
//     );
//     return true;
//   } else {
//     return false;
//   }
// }

// receiving message from extension - works
// window.addEventListener(
//   "message",
//   function (event) {
//     // We only accept messages from ourselves
//     if (event.source != window) return;

//     if (event.data.type && event.data.type == "FROM_CONTENT") {
//       console.log("Page script received: " + event.data.text);
//     }
//   },
//   false
// );
</script>
